import {
  type Config,
  etaDisplay,
  fmtDate,
  fmtDateTime,
  type PeopleMap,
  type QueueTicket,
} from '@hrms/shared'
import type { HTMLAttributes, Ref } from 'react'
import { Avatar } from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import { CategoryChip, LockIcon, PersonAvatar, PriorityBars, personName } from '../tickets/ui.tsx'

/** ETA hint on a card (DESIGN.md §3.2): nothing when done, "ETA paused", "Overdue", "Due 29 Oct". */
function CardEta({ ticket, config, now }: { ticket: QueueTicket; config: Config; now: number }) {
  const eta = etaDisplay(ticket, { now, config, viewer: 'hr' })
  switch (eta.kind) {
    case 'paused':
      return (
        <span
          data-tour="card-eta"
          className="text-xs text-amber-fg"
          {...hint('Paused: the ETA waits while HR waits on the employee')}
        >
          ETA paused
        </span>
      )
    case 'overdue':
      return (
        <span
          data-tour="card-eta"
          className="text-xs font-medium text-danger"
          {...hint(`Overdue: was expected by ${fmtDateTime(eta.dueAt, { now })}`)}
        >
          Overdue
        </span>
      )
    case 'due':
      return (
        <span
          data-tour="card-eta"
          className="text-xs text-muted"
          {...hint(`Expected by ${fmtDateTime(eta.dueAt, { now })}`)}
        >
          Due {fmtDate(eta.dueAt, { now })}
        </span>
      )
    default:
      return null
  }
}

export interface BoardCardProps extends HTMLAttributes<HTMLDivElement> {
  ticket: QueueTicket
  people: PeopleMap
  config: Config
  now: number
  /** Drag source left behind while the overlay follows the pointer. */
  ghost?: boolean
  /** Rendered in the drag overlay. */
  overlay?: boolean
  draggable?: boolean
  ref?: Ref<HTMLDivElement>
}

/** One ticket on the board. Clicking or pressing Enter opens it (the caller wires `onClick`). */
export function BoardCard({
  ticket: t,
  people,
  config,
  now,
  ghost,
  overlay,
  draggable,
  className,
  ...rest
}: BoardCardProps) {
  const assignee = t.assignee ? people[t.assignee] : undefined
  return (
    <div
      data-tour="board-card"
      {...rest}
      className={cn(
        'block w-full rounded-card border border-border bg-surface px-3 py-2.5 text-left transition-[border-color,box-shadow] outline-none hover:border-border-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        draggable ? 'cursor-grab touch-manipulation' : 'cursor-pointer',
        ghost && 'opacity-40',
        overlay && 'cursor-grabbing shadow-pop',
        className,
      )}
    >
      <div className="flex items-start gap-1.5">
        {t.confidential && <LockIcon className="mt-[3px] shrink-0" />}
        <span className="min-w-0 font-medium break-words">{t.title}</span>
      </div>
      <div className="mt-1.5">
        <CategoryChip categoryId={t.categoryId} categories={config.categories} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="tabular text-[12.5px] text-muted">{t.key}</span>
        <PriorityBars priorityId={t.priorityId} priorities={config.priorities} showName={false} />
        <span className="ml-auto flex items-center gap-2">
          <CardEta ticket={t} config={config} now={now} />
          {t.assignee ? (
            <span
              className="inline-flex"
              {...hint(`Assigned to ${personName(people, t.assignee)}`)}
            >
              <PersonAvatar person={assignee} id={t.assignee} />
            </span>
          ) : (
            <span className="inline-flex" {...hint('Unassigned')}>
              <Avatar />
            </span>
          )}
        </span>
      </div>
    </div>
  )
}
